import React, { useState } from 'react';
import propTypes from 'prop-types';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Table, Alert, Progress, Input, Label, FormGroup,
} from 'reactstrap';
import { fetchVouchersByGroup } from '../../../utils/voucherBulkManage';
import { EDITABLE_FIELDS, buildEditPreview, runEdit } from '../../../utils/voucherBulkEdit';

const STEP = { PICK: 'pick', LOADING: 'loading', PREVIEW: 'preview', RUNNING: 'running', DONE: 'done' };

export default function ModalBulkVoucherEdit(props) {
  const { isOpen, onClose, onCompleted } = props;
  const [step, setStep] = useState(STEP.PICK);
  const [group, setGroup] = useState('');
  const [enabled, setEnabled] = useState({});
  const [values, setValues] = useState({});
  const [vouchers, setVouchers] = useState([]);
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const reset = () => {
    setStep(STEP.PICK); setVouchers([]); setRows([]); setResults([]);
    setProgress({ done: 0, total: 0 }); setError('');
  };
  const close = () => { reset(); setGroup(''); setEnabled({}); setValues({}); onClose(); };

  const overrides = () => {
    const o = {};
    EDITABLE_FIELDS.forEach((f) => { if (enabled[f.key]) o[f.key] = values[f.key] !== undefined ? values[f.key] : ''; });
    return o;
  };

  const loadPreview = async () => {
    const g = group.trim();
    if (!g) { setError('Enter a group_name.'); return; }
    const o = overrides();
    if (!Object.keys(o).length) { setError('Tick at least one field to change.'); return; }
    setError(''); setStep(STEP.LOADING); setProgress({ done: 0, total: 0 });
    try {
      const vs = await fetchVouchersByGroup(g);
      if (!vs.length) { setError(`No vouchers found in group "${g}".`); setStep(STEP.PICK); return; }
      const pr = await buildEditPreview(vs, o, (done, total) => setProgress({ done, total }));
      setVouchers(vs); setRows(pr); setStep(STEP.PREVIEW);
    } catch (err) {
      setError(`Could not load: ${(err && err.message) || 'request failed'}`);
      setStep(STEP.PICK);
    }
  };

  const actionable = rows.filter((r) => !r.skipped);
  const typeChanged = !!enabled.voucher_type;

  const execute = async () => {
    setStep(STEP.RUNNING); setProgress({ done: 0, total: actionable.length });
    const res = await runEdit(vouchers, rows, overrides(), (done, total) => setProgress({ done, total }));
    setResults(res); setStep(STEP.DONE);
    if (onCompleted) onCompleted();
  };

  return (
    <Modal isOpen={isOpen} toggle={close} size="lg">
      <ModalHeader toggle={close}>Bulk Edit Vouchers by Group</ModalHeader>
      <ModalBody>
        {step === STEP.PICK && (
          <div>
            <FormGroup>
              <Label>Group name</Label>
              <Input value={group} onChange={(e) => setGroup(e.target.value)} placeholder="e.g. RAMADAN25" />
            </FormGroup>
            <p className="mb-2"><strong>Fields to change</strong> <small className="text-muted">(tick a field to edit it; unticked fields are left as-is)</small></p>
            <div>
              {EDITABLE_FIELDS.map((f) => {
                const on = !!enabled[f.key];
                return (
                  <div
                    key={f.key}
                    className="d-flex align-items-center py-2 px-2"
                    style={{ borderBottom: '1px solid #e9ecef', background: on ? '#f1f8ff' : 'transparent' }}
                  >
                    <FormGroup check className="mb-0 mr-2" style={{ width: 210 }}>
                      <Label check className="mb-0" style={{ cursor: 'pointer', fontWeight: on ? 600 : 400 }}>
                        <Input type="checkbox" checked={on} onChange={(e) => setEnabled({ ...enabled, [f.key]: e.target.checked })} />
                        {' '}{f.label}{f.flag && <span className="text-warning"> *</span>}
                      </Label>
                    </FormGroup>
                    <div style={{ flex: 1 }}>
                      {!on ? (
                        <span className="text-muted font-italic">unchanged</span>
                      ) : f.kind === 'select' || f.kind === 'bool' ? (
                        <Input bsSize="sm" type="select" value={values[f.key] !== undefined ? values[f.key] : (f.kind === 'bool' ? '0' : (f.options ? f.options[0] : ''))} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}>
                          {f.kind === 'bool'
                            ? [<option key="1" value="1">yes</option>, <option key="0" value="0">no</option>]
                            : f.options.map((opt) => <option key={opt} value={opt}>{(f.optionLabels && f.optionLabels[opt]) || opt || 'All'}</option>)}
                        </Input>
                      ) : (
                        <Input bsSize="sm" value={values[f.key] || ''} placeholder={f.kind === 'datetime' ? 'YYYY-MM-DD HH:mm' : 'new value'} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {typeChanged && <Alert color="warning">Changing voucher type alters how the voucher is reached (assignment vs code vs auto-listed). Review carefully.</Alert>}
            {error && <Alert color="danger">{error}</Alert>}
          </div>
        )}

        {step === STEP.LOADING && (
          <div>
            <p>Loading group and checking usage {progress.done}/{progress.total}...</p>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
          </div>
        )}

        {step === STEP.PREVIEW && (
          <div>
            <p>Group <strong>{group.trim()}</strong> &mdash; {rows.length} matched, {actionable.length} will change.</p>
            <Table size="sm" bordered responsive>
              <thead><tr><th>Code</th><th>Name</th><th>Changes (old &rarr; new)</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={r.skipped ? 'text-muted' : ''}>
                    <td>{r.code}</td>
                    <td>{r.name}</td>
                    <td>{r.skipped ? `skip: ${r.skipReason}` : r.changes.map((c) => `${c.field}: ${c.old} → ${c.next}`).join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {actionable.length === 0 && <Alert color="warning">Nothing to change, every matched voucher is skipped.</Alert>}
          </div>
        )}

        {step === STEP.RUNNING && (
          <div>
            <p>Updating {progress.done}/{progress.total}...</p>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
          </div>
        )}

        {step === STEP.DONE && (
          <div>
            <Alert color={results.some((r) => r.status === 'failed') ? 'danger' : 'success'}>
              {results.filter((r) => r.status === 'ok').length} voucher(s) updated.
              {results.some((r) => r.status === 'failed') && ' Stopped on a failure.'}
            </Alert>
            <Table size="sm" bordered responsive>
              <thead><tr><th>Code</th><th>Status</th><th>Note</th></tr></thead>
              <tbody>
                {results.map((r, i) => (<tr key={i}><td>{r.code}</td><td>{r.status}</td><td>{r.error || ''}</td></tr>))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {step === STEP.PICK && <Button color="primary" onClick={loadPreview}>Preview changes</Button>}
        {step === STEP.PREVIEW && <Button color="primary" disabled={!actionable.length} onClick={execute}>Apply to {actionable.length} voucher(s)</Button>}
        {step === STEP.PREVIEW && <Button color="secondary" onClick={reset}>Back</Button>}
        {step === STEP.DONE && <Button color="secondary" onClick={reset}>Do another</Button>}
        <Button color="link" onClick={close} disabled={step === STEP.RUNNING}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}

ModalBulkVoucherEdit.propTypes = {
  isOpen: propTypes.bool,
  onClose: propTypes.func.isRequired,
  onCompleted: propTypes.func,
};
ModalBulkVoucherEdit.defaultProps = { isOpen: false };
